import logger from '@eventbrite/client-logger';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { asyncWait } from '@eventbrite/wait';
import { OrderDocumentStatus } from '../../api/constants';
import { getOrderStatus } from '../../api/order';
import {
    ORDER_STATUS_POLLING_PERIOD_IN_MS,
    ORDER_STATUS_POLLING_TIMEOUT_IN_MS,
    SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS,
} from './constants';

export const waitUntilOrderIsProcessed = async (
    orderId: string,
    env: 'dev' | 'qa' | 'prod',
) => {
    const startTime = Date.now();
    const isTimerExpired = () =>
        Date.now() - startTime > ORDER_STATUS_POLLING_TIMEOUT_IN_MS;
    const isPollingBeingSlow = () =>
        Date.now() - startTime > SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS;
    const isOrderProcessed = (status: OrderDocumentStatus | null) =>
        status &&
        [
            OrderDocumentStatus.complete,
            OrderDocumentStatus.cancelled,
            OrderDocumentStatus.retryPayment,
        ].includes(status);

    let lastStatus: OrderDocumentStatus | null = null;
    let hasReportedSlowRequestToSentry = false;

    while (!isTimerExpired()) {
        const pollingPeriodPromise = asyncWait(
            ORDER_STATUS_POLLING_PERIOD_IN_MS,
        );

        lastStatus = await getOrderStatus(orderId, env).catch((error) => {
            const wrappedError = new Error(
                `Error polling CheckoutStatusAPI: ${error}`,
            );
            logger.error(wrappedError);
            return null;
        });

        if (isOrderProcessed(lastStatus)) {
            return lastStatus;
        }

        if (!hasReportedSlowRequestToSentry && isPollingBeingSlow()) {
            hasReportedSlowRequestToSentry = true;
            logger.error(
                new Error(
                    `The order is taking more than ${SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS}ms to finish (last status was ${lastStatus})`,
                ),
            );
        }

        await pollingPeriodPromise;
    }

    logger.error(
        new Error(
            `The order did not transition to complete, cancelled or error status on time (last status was ${lastStatus})`,
        ),
    );

    throw new CustomSubmissionError(
        {
            _error: 'There was a problem submitting your order. Please try again.',
        },
        {},
    );
};
